import React from 'react';
import { AlertTriangle, RefreshCw, Upload, X } from 'lucide-react';

export default function CMSConflictModal({
  isOpen,
  conflictData = {},
  onReloadServerVersion,
  onOverwrite,
  onClose,
}) {
  if (!isOpen) return null;

  const currentRecord = conflictData.current_record || {};
  const currentUpdatedAt = conflictData.current_updated_at;
  const expectedUpdatedAt = conflictData.expected_updated_at;

  const formatDate = (isoString) => {
    if (!isoString) return 'Unknown';
    try {
      return new Date(isoString).toLocaleString('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'medium',
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-amber-300 w-full max-w-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 text-amber-800 rounded-xl">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-amber-950">Concurrent Edit Conflict</h3>
              <p className="text-xs text-amber-700">Someone else modified this page recently</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 text-xs text-slate-600">
          <p className="text-slate-700 font-medium leading-relaxed">
            The page you are editing was modified by another editor or tab while you were working.
            To avoid accidentally overwriting their changes, choose how you would like to proceed:
          </p>

          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5 font-mono text-[11px]">
            <div className="flex justify-between text-slate-500">
              <span>Your editing session opened at:</span>
              <span className="text-slate-800 font-semibold">{formatDate(expectedUpdatedAt)}</span>
            </div>
            <div className="flex justify-between text-amber-800">
              <span>Latest server modification at:</span>
              <span className="font-bold">{formatDate(currentUpdatedAt)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 pt-2">
            {/* Option A: Reload Server Version */}
            <button
              type="button"
              onClick={() => onReloadServerVersion(currentRecord)}
              className="w-full p-3.5 rounded-xl border-2 border-[#2B3380]/30 hover:border-[#2B3380] bg-[#2B3380]/5 hover:bg-[#2B3380]/10 text-left transition flex items-start gap-3"
            >
              <RefreshCw className="w-4 h-4 text-[#2B3380] mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold text-[#2B3380]">Reload latest server version (Recommended)</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Discard your unsaved local edits and review the latest content saved on the server.
                </div>
              </div>
            </button>

            {/* Option B: Force Overwrite */}
            <button
              type="button"
              onClick={() => onOverwrite(currentUpdatedAt)}
              className="w-full p-3.5 rounded-xl border border-rose-200 hover:border-rose-400 bg-rose-50/50 hover:bg-rose-50 text-left transition flex items-start gap-3"
            >
              <Upload className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-bold text-rose-700">Overwrite server version with my edits</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Force save your current form. The conflicting server updates will be replaced.
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
          >
            Cancel & Keep Editing
          </button>
        </div>
      </div>
    </div>
  );
}
